import { downloadUrlResponseSchema } from '@datum/shared'
import { useEffect, useState } from 'react'
import { api } from './api'

/**
 * Signed download URLs for R2 attachments. The API signs URLs with a 15-minute
 * TTL, so cache with a safety margin and dedupe in-flight requests per
 * attachment (an editor screen may render several blocks at once).
 */

const FRESH_MS = 10 * 60 * 1000

const cache = new Map<string, { url: string; fetchedAt: number }>()
const inflight = new Map<string, Promise<string | null>>()

export function attachmentUrl(attachmentId: string): Promise<string | null> {
  const hit = cache.get(attachmentId)
  if (hit && Date.now() - hit.fetchedAt < FRESH_MS) return Promise.resolve(hit.url)
  const running = inflight.get(attachmentId)
  if (running) return running

  const task = (async () => {
    try {
      const response = await api.api.media.attachments[':attachment_id']['download-url'].$post({
        param: { attachment_id: attachmentId },
      })
      if (!response.ok) return null
      const parsed = downloadUrlResponseSchema.safeParse(await response.json())
      if (!parsed.success) return null
      cache.set(attachmentId, { url: parsed.data.download_url, fetchedAt: Date.now() })
      return parsed.data.download_url
    } catch {
      return null
    } finally {
      inflight.delete(attachmentId)
    }
  })()

  inflight.set(attachmentId, task)
  return task
}

export function useAttachmentUrl(attachmentId: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!attachmentId) return null
    const hit = cache.get(attachmentId)
    return hit && Date.now() - hit.fetchedAt < FRESH_MS ? hit.url : null
  })

  useEffect(() => {
    if (!attachmentId) {
      setUrl(null)
      return
    }
    let active = true
    void attachmentUrl(attachmentId).then((next) => {
      if (active) setUrl(next)
    })
    return () => {
      active = false
    }
  }, [attachmentId])

  return url
}
